import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ConversationAPI } from 'api/conversations';
import { ProjectId } from 'api/models';
import { ConversationPane } from 'components/organisms/conversations';
import {
  DocumentTabContainer,
  StyledTabPane,
  StyledTabs,
} from 'components/organisms/document/parts/document-tabs';
import {
  ConversationsTabLabel,
  TasksTabLabel,
} from 'components/organisms/document/parts/document-tabs/tab-labels';
import { useCurrentSpace } from 'components/organisms/spaces/context';
import {
  AbsolutePrivatePlacard,
  PrivatePlacard,
  PrivatePlacardHolder,
} from 'components/organisms/spaces/space-tab/styles';
import {
  DocumentTabsFreeMode,
  selectDocumentTabsFreeMode,
  toggleDocumentTabsFreeMode,
} from 'data/layout';
import { useTasksByIds } from 'data/tasks/hooks';
import { useGetConversationWithWS } from 'hooks/use-conversations';
import { useCurrentProject } from 'hooks/use-current-project';
import { useToggleFreeMode } from 'hooks/use-toggle-free-mode';
import { useUser } from 'hooks/use-user';
import { useCurrentSpaceTab } from '../context';
import { TasksTab } from './tasks-tab';

const EmbeddedTabPane = styled(StyledTabPane)`
  height: calc(var(--document-tab-pane-height));
`;

export const PopoverContent: FC = () => {
  const { account } = useUser();
  const project = useCurrentProject();
  const spaceTab = useCurrentSpaceTab();
  const { private: isPrivate } = useCurrentSpace();
  const [activeTab, setActiveTab] = useState('conversations');

  const dispatch = useDispatch();
  const documentsTabsActive = useSelector(selectDocumentTabsFreeMode);
  const onToggle = (value: boolean) => {
    dispatch(toggleDocumentTabsFreeMode(value));
  };
  const documentTabsFreeHandler = useToggleFreeMode({
    active: documentsTabsActive,
    localStorageKey: DocumentTabsFreeMode,
    onToggle,
  });

  const { data: commentsData } = useGetConversationWithWS(project.projectId, {
    type: 'SPACE_TAB',
    id: spaceTab.spaceTabId,
  });

  const comments = useMemo(() => commentsData?.comments || [], [commentsData]);

  function onTabClickHandler(tabClicked) {
    if (!documentTabsFreeHandler.active && tabClicked === activeTab) {
      documentTabsFreeHandler.enter();
    } else {
      documentTabsFreeHandler.exit();
    }
  }

  const { projectId } = useCurrentProject();
  const { taskList } = useCurrentSpaceTab();

  const tasks = useTasksByIds(ProjectId(projectId), taskList);

  return (
    <DocumentTabContainer $hidePaneContent={documentTabsFreeHandler.active}>
      <StyledTabs
        activeKey={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
        }}
        onTabClick={onTabClickHandler}
        tabPosition="right"
        tabBarStyle={{
          minWidth: 'var(--tabs-width)',
          width: 'var(--tabs-width)',
          zIndex: 10,
        }}
      >
        <EmbeddedTabPane
          key="conversations"
          tab={<ConversationsTabLabel count={comments.length} />}
        >
          <PrivatePlacardHolder>
            {isPrivate && (
              <AbsolutePrivatePlacard>
                <FormattedMessage id="document.private.conversations" />
              </AbsolutePrivatePlacard>
            )}
          </PrivatePlacardHolder>
          <ConversationPane
            spotlights={[]}
            context={{ type: 'SPACE_TAB', id: spaceTab.spaceTabId }}
            projectId={project.projectId}
            accountId={account.accountId}
            apiService={ConversationAPI}
          />
        </EmbeddedTabPane>
        <EmbeddedTabPane
          key="tasks"
          tab={<TasksTabLabel count={tasks.length} />}
        >
          {isPrivate && (
            <PrivatePlacard>
              <FormattedMessage id="document.private.tasks" />
            </PrivatePlacard>
          )}
          <TasksTab />
        </EmbeddedTabPane>
      </StyledTabs>
    </DocumentTabContainer>
  );
};
